import { useMemo } from 'react';
import type { StackedLineChartChannel } from '../../../../utils/charts/StackedLineChart';
import { StackedLineChart } from '../../../../utils/charts/StackedLineChart';

export function MetricsOverviewStackedLineChart<T extends { date: Date }>({
	channels,
	chartData,
	dateFilter,
	tooltipDataRenderer,
	dateRangeOption,
	yAxisFormatter,
	onClick,
	showEmptyStateMessage: externalShowEmptyStateMessage,
	emptyStateMessage: externalEmptyStateMessage,
	maxChannels,
}: {
	channels: StackedLineChartChannel<T, Date, number>[];
	chartData: T[];
	dateFilter: string;
	tooltipDataRenderer: (dataIndex: number) => JSX.Element | null;
	dateRangeOption?: string;
	yAxisFormatter?: (value: number) => string;
	onClick?: (dataIndex: number) => void;
	showEmptyStateMessage?: boolean;
	emptyStateMessage?: { title: string; description: string };
	maxChannels?: number;
}) {
	const isInvalidCombination = useMemo(() => {
		// Check for month range with short time periods
		if (
			dateRangeOption === 'month' &&
			['24h', '7d', '30d'].includes(dateFilter)
		) {
			return true;
		}

		// Check for week range with very short time periods
		if (dateRangeOption === 'week' && ['24h', '7d'].includes(dateFilter)) {
			return true;
		}

		return false;
	}, [dateRangeOption, dateFilter]);

	const invalidEmptyStateMessage = useMemo(
		() =>
			dateRangeOption === 'month'
				? {
						title: 'Monthly view is not available',
						description:
							'Please select a time range of 3 months or more to use this view',
					}
				: {
						title: 'Weekly view is not available',
						description:
							'Please select a time range of 30 days or more to use this view',
					},
		[dateRangeOption]
	);

	const showEmptyStateMessage = useMemo(
		() =>
			externalShowEmptyStateMessage === true ||
			(externalShowEmptyStateMessage === undefined && isInvalidCombination),
		[externalShowEmptyStateMessage, isInvalidCombination]
	);

	const emptyStateMessage = useMemo(
		() =>
			externalEmptyStateMessage ??
			(isInvalidCombination ? invalidEmptyStateMessage : undefined),
		[externalEmptyStateMessage, isInvalidCombination, invalidEmptyStateMessage]
	);

	return (
		<StackedLineChart
			channels={channels}
			data={isInvalidCombination ? [] : chartData}
			width="100%"
			height={300}
			maxChannels={maxChannels ?? 6}
			xAxis={{
				formatter: (date: Date) => {
					// For 24h view, show time
					if (dateFilter === '24h') {
						return date.toLocaleTimeString([], {
							hour: '2-digit',
							minute: '2-digit',
						});
					}

					// For longer ranges (90d+), show month and year
					if (['90d', '180d', '365d'].includes(dateFilter)) {
						return date.toLocaleDateString([], {
							month: 'short',
							year: 'numeric',
						});
					}

					// For other views (7d, 30d), show date
					return date.toLocaleDateString([], {
						month: 'short',
						day: 'numeric',
					});
				},
				maxTicks: dateFilter === '90d' ? 3 : 7,
			}}
			yAxis={{
				formatter: yAxisFormatter || ((value: number) => value.toString()),
				grid: true,
			}}
			chartProps={{
				y: {
					zero: true,
					nice: true,
				},
				x: {
					domain:
						chartData.length > 0
							? [chartData[0].date, chartData[chartData.length - 1].date]
							: undefined,
				},
				marginLeft: 60,
				marginRight: 30,
				marginTop: 20,
				marginBottom: 40,
				style: {
					background: 'transparent',
					overflow: 'visible',
				},
			}}
			legend={{
				position: 'bottom',
				visible: true,
			}}
			tooltipRenderer={tooltipDataRenderer}
			showEmptyStateMessage={showEmptyStateMessage}
			emptyStateMessage={emptyStateMessage}
			onClick={onClick}
		/>
	);
}
